import AdminLayout from '../components/AdminLayout';

const accessRules = [
  {
    icon: 'ri-eye-line',
    title: 'Preview All Files',
    description: 'All authenticated users can preview any file in the resource library without restriction.',
    status: 'enabled',
  },
  {
    icon: 'ri-download-line',
    title: 'Download All Files',
    description: 'All authenticated users can download any file in the resource library without restriction.',
    status: 'enabled',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Watermark Protection',
    description: 'All previewed documents are overlaid with a dynamic watermark containing the user\'s email, company, and timestamp.',
    status: 'enabled',
  },
  {
    icon: 'ri-lock-password-line',
    title: 'Login Required',
    description: 'Access to the resource library requires a valid account. Unauthenticated users are redirected to the login page.',
    status: 'enabled',
  },
];

const policyPoints = [
  'Every registered user has identical access rights — no tiers, no restrictions.',
  'Admins control access at the account level: enable or disable accounts via User Management.',
  'Disabling an account immediately revokes all access to the portal.',
  'No category-level or file-level permission controls are applied.',
];

export default function PermissionsPage() {
  return (
    <AdminLayout title="Access Policy" subtitle="Unified permissions applied to all users">
      <div className="max-w-3xl space-y-6">

        {/* Policy Banner */}
        <div className="rounded-xl p-5 border flex items-start gap-4" style={{ background: 'rgba(0,105,189,0.04)', borderColor: 'rgba(0,105,189,0.15)' }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(0,105,189,0.1)' }}>
            <i className="ri-shield-check-line text-lg" style={{ color: '#0069BD' }}></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-1">Unified Access Model</h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              This platform uses a flat permission model. All active users share the same access rights — full preview and download access to all resources. Access control is managed at the account level only.
            </p>
          </div>
        </div>

        {/* Access Rules */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-800">Active Access Rules</h3>
            <p className="text-xs text-gray-400 mt-0.5">These rules apply uniformly to all authenticated users</p>
          </div>
          <div className="divide-y divide-gray-50">
            {accessRules.map((rule) => (
              <div key={rule.title} className="flex items-center gap-4 px-5 py-4">
                <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                  <i className={`${rule.icon} text-base text-gray-500`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{rule.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{rule.description}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#0069BD' }}></div>
                  <span className="text-xs font-semibold" style={{ color: '#0069BD' }}>Enabled</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Policy Summary */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-800">Policy Summary</h3>
          </div>
          <div className="px-5 py-4 space-y-3">
            {policyPoints.map((point, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(0,105,189,0.08)' }}>
                  <i className="ri-check-line text-xs" style={{ color: '#0069BD' }}></i>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How to Revoke Access */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-800">How to Revoke Access</h3>
          </div>
          <div className="px-5 py-5">
            <div className="flex flex-col sm:flex-row gap-4">
              {[
                { step: '1', label: 'Go to User Management', icon: 'ri-team-line', desc: 'Navigate to the User Management section in the sidebar.' },
                { step: '2', label: 'Find the user', icon: 'ri-search-line', desc: 'Search by name, email, or company to locate the account.' },
                { step: '3', label: 'Disable the account', icon: 'ri-forbid-line', desc: 'Click "Disable" — the user loses access immediately.' },
              ].map((s) => (
                <div key={s.step} className="flex-1 flex flex-col items-start gap-2 p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: '#0069BD' }}>
                      {s.step}
                    </div>
                    <p className="text-sm font-semibold text-gray-800">{s.label}</p>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </AdminLayout>
  );
}
